import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { api, ApiError } from "@/lib/apiClient";
import { usePortalAuth } from "@/lib/auth";

type Step = "email" | "code";

const RESEND_COOLDOWN_SEC = 120;

function formatCountdown(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function LoginPage() {
  const { user, loading, refresh } = usePortalAuth();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setInterval(() => {
      setResendIn((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendIn]);

  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  async function requestCode(): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/portal/request-code", { email: email.trim() });
      setInfo(
        "Falls die Adresse bei uns hinterlegt ist, wurde ein Anmeldecode per E-Mail gesendet.",
      );
      setResendIn(RESEND_COOLDOWN_SEC);
      setStep("code");
      return true;
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Code konnte nicht angefordert werden.",
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function onRequestCode(e: FormEvent) {
    e.preventDefault();
    setInfo(null);
    await requestCode();
  }

  async function onResendCode() {
    if (resendIn > 0 || busy) return;
    setCode("");
    setInfo(null);
    await requestCode();
  }

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/portal/verify-code", {
        email: email.trim(),
        code: code.trim(),
      });
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Anmeldung fehlgeschlagen.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="portal-stage relative min-h-screen overflow-hidden">
      <div className="portal-grid pointer-events-none absolute inset-0 opacity-90" />
      <div
        className="pointer-events-none absolute -right-[20vmax] -top-[16vmax] h-[58vmax] w-[58vmax] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(226, 160, 8, 0.28), transparent 64%)",
        }}
      />

      <main className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <p className="font-cond text-5xl font-bold uppercase tracking-wide text-ink sm:text-6xl">
          Ernsthaus
        </p>
        <h1 className="mt-2 font-cond text-xl font-semibold uppercase tracking-[0.42em] text-[#9a6c00]">
          Kundenportal
        </h1>
        <p className="mt-4 text-base text-muted">
          Melden Sie sich mit der E-Mail-Adresse an, die bei uns hinterlegt ist.
          Sie erhalten einen einmaligen Anmeldecode.
        </p>

        {step === "email" ? (
          <form onSubmit={onRequestCode} className="mt-8 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-ink">
                E-Mail
              </span>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="name@firma.de"
                className="w-full rounded border border-black/15 bg-white/80 px-3 py-2.5 text-ink outline-none ring-signal focus:ring-2"
              />
            </label>
            {error && (
              <p className="text-sm font-medium text-red-600" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-sm bg-ink px-4 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-black disabled:opacity-60"
            >
              {busy ? "Wird gesendet …" : "Code anfordern"}
            </button>
          </form>
        ) : (
          <form onSubmit={onVerifyCode} className="mt-8 space-y-4">
            {info && (
              <p className="rounded border border-signal/40 bg-signal/10 px-3 py-2 text-sm text-ink">
                {info}
              </p>
            )}
            <p className="text-sm text-muted">
              Code für <strong className="text-ink">{email}</strong>
            </p>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-ink">
                Anmeldecode
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(ev) => setCode(ev.target.value)}
                placeholder="6-stelliger Code"
                className="w-full rounded border border-black/15 bg-white/80 px-3 py-2.5 tracking-[0.3em] text-ink outline-none ring-signal focus:ring-2"
              />
            </label>
            {error && (
              <p className="text-sm font-medium text-red-600" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-sm bg-ink px-4 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-black disabled:opacity-60"
            >
              {busy ? "Wird geprüft …" : "Anmelden"}
            </button>

            {resendIn > 0 ? (
              <p className="text-center text-sm text-muted">
                Code erneut senden in {formatCountdown(resendIn)}
              </p>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => void onResendCode()}
                className="w-full rounded-sm border border-black/15 bg-white/70 px-4 py-3 text-sm font-bold uppercase tracking-wide text-ink transition hover:bg-white disabled:opacity-60"
              >
                {busy ? "Wird gesendet …" : "Code erneut senden"}
              </button>
            )}

            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
                setInfo(null);
                setResendIn(0);
              }}
              className="w-full text-sm font-semibold text-muted underline-offset-2 hover:underline"
            >
              Andere E-Mail verwenden
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
